/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  HostBinding,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UmssDatePickerHeaderComponent } from '../date-picker-header/date-picker-header.component';
import { UmssDatePickerInputComponent } from '../date-picker-input/date-picker-input.component';

export interface DateRangePickerDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isRangeStart: boolean;
  isRangeEnd: boolean;
  isInRange: boolean;
  isDisabled: boolean;
  isWeekend: boolean;
}

export interface DateRangePickerWeek {
  days: DateRangePickerDay[];
}

@Component({
  selector: 'umss-date-range-picker',
  standalone: true,
  imports: [
    CommonModule,
    UmssDatePickerHeaderComponent,
    UmssDatePickerInputComponent
  ],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssDateRangePickerComponent),
      multi: true
    }
  ]
})
export class UmssDateRangePickerComponent
  implements ControlValueAccessor, OnInit, OnDestroy, OnChanges
{
  @HostBinding('style.display') display = 'contents';
  @ViewChild('datePickerInput', { read: ElementRef })
  datePickerInputRef?: ElementRef;

  private _generatedId = `umss-date-range-picker-${Math.random()
    .toString(36)
    .substring(2, 11)}`;
  private static openInstances: UmssDateRangePickerComponent[] = [];
  private boundHandleDocumentClick: (event: MouseEvent) => void;
  private boundHandleFocusIn: (event: FocusEvent) => void;

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
    this.generateCalendar();
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    this.boundHandleFocusIn = this.handleFocusIn.bind(this);
  }

  ngOnInit(): void {
    if (this._startDate) {
      this.currentMonth = new Date(
        this._startDate.getFullYear(),
        this._startDate.getMonth(),
        1
      );
      this.generateWeeks();
    }

    // Manually attach document listeners since @HostListener might not work with display:contents
    document.addEventListener('click', this.boundHandleDocumentClick);
    document.addEventListener('focusin', this.boundHandleFocusIn);
  }

  ngOnDestroy(): void {
    // Clean up the event listeners
    document.removeEventListener('click', this.boundHandleDocumentClick);
    document.removeEventListener('focusin', this.boundHandleFocusIn);
    this.unregisterOpenInstance();
  }

  ngOnChanges(changes: SimpleChanges): void {
    let shouldRegenerate = false;

    if (changes['startDate'] && !changes['startDate'].firstChange) {
      this._startDate = changes['startDate'].currentValue;
      if (this._startDate) {
        this.currentMonth = new Date(
          this._startDate.getFullYear(),
          this._startDate.getMonth(),
          1
        );
      }
      shouldRegenerate = true;
    }

    if (changes['endDate'] && !changes['endDate'].firstChange) {
      this._endDate = changes['endDate'].currentValue;
      shouldRegenerate = true;
    }

    if (shouldRegenerate) {
      this.generateWeeks();
    }
  }

  /** Content props */
  @Input() id?: string;
  @Input() label?: string;
  @Input() placeholder?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() variant: 'Default' | 'Inline' = 'Default';

  /** State props */
  @Input() set startDate(value: Date | undefined) {
    this._startDate = value;
    this.generateWeeks();
  }
  get startDate(): Date | undefined {
    return this._startDate;
  }

  @Input() set endDate(value: Date | undefined) {
    this._endDate = value;
    this.generateWeeks();
  }
  get endDate(): Date | undefined {
    return this._endDate;
  }

  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;

  /** Calendar props */
  @Input() showWeekNumbers: boolean = false;
  @Input() firstDayOfWeek: 0 | 1 = 1; // 0 = Sunday, 1 = Monday
  @Input() locale: string = 'en-US';

  /** Event outputs */
  @Output() startDateChange = new EventEmitter<Date | undefined>();
  @Output() endDateChange = new EventEmitter<Date | undefined>();
  @Output() selectedRangeChange = new EventEmitter<{
    start: Date | undefined;
    end: Date | undefined;
  }>();
  @Output() monthChange = new EventEmitter<Date>();
  @Output() yearChange = new EventEmitter<Date>();

  /** Internal state */
  _startDate?: Date;
  _endDate?: Date;

  // Hover state for range preview (optional enhancement, skipping for now to keep simple)

  private onChange = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    value: { start: Date | undefined; end: Date | undefined } | undefined
  ) => {};
  private onTouched = () => {};

  /** Calendar state */
  currentMonth: Date = new Date();
  weeks: DateRangePickerWeek[] = [];
  weekdayNames: string[] = [];
  monthNames: string[] = [];
  isCalendarOpen: boolean = false;
  calendarTop: string = '0px';
  calendarLeft: string = '0px';

  /** Computed properties */
  get computedId(): string {
    return this.id || this._generatedId;
  }

  get formattedDateRange(): string {
    if (!this._startDate) return '';
    const startStr = this._startDate.toLocaleDateString(this.locale);
    if (!this._endDate) return startStr;
    const endStr = this._endDate.toLocaleDateString(this.locale);
    return `${startStr} - ${endStr}`;
  }

  get currentMonthName(): string {
    return this.currentMonth.toLocaleDateString(this.locale, { month: 'long' });
  }

  get currentYear(): number {
    return this.currentMonth.getFullYear();
  }

  /** Calendar generation */
  generateCalendar(): void {
    this.generateWeekdayNames();
    this.generateMonthNames();
    this.generateWeeks();
  }

  private generateWeekdayNames(): void {
    const date = new Date();
    const firstDay = this.firstDayOfWeek;
    this.weekdayNames = [];

    for (let i = 0; i < 7; i++) {
      const dayIndex = (firstDay + i) % 7;
      date.setDate(date.getDate() - date.getDay() + dayIndex);
      this.weekdayNames.push(
        date.toLocaleDateString(this.locale, { weekday: 'short' })
      );
    }
  }

  private generateMonthNames(): void {
    this.monthNames = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(2023, i, 1);
      this.monthNames.push(
        date.toLocaleDateString(this.locale, { month: 'long' })
      );
    }
  }

  private generateWeeks(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const today = new Date();

    // Calculate the first day to show (might be from previous month)
    const firstDayToShow = new Date(firstDayOfMonth);
    const dayOfWeek = (firstDayOfMonth.getDay() - this.firstDayOfWeek + 7) % 7;
    firstDayToShow.setDate(firstDayToShow.getDate() - dayOfWeek);

    this.weeks = [];
    const currentDate = new Date(firstDayToShow);

    // Generate 6 weeks to ensure we always show a complete calendar
    for (let week = 0; week < 6; week++) {
      const weekDays: DateRangePickerDay[] = [];

      for (let day = 0; day < 7; day++) {
        const isCurrentMonth = currentDate.getMonth() === month;
        const isToday = this.isSameDay(currentDate, today);
        const isDisabled = this.isDateDisabled(currentDate);
        const isWeekend =
          currentDate.getDay() === 0 || currentDate.getDay() === 6;

        let isRangeStart = false;
        let isRangeEnd = false;
        let isInRange = false;

        if (this._startDate) {
          isRangeStart = this.isSameDay(currentDate, this._startDate);
        }
        if (this._endDate) {
          isRangeEnd = this.isSameDay(currentDate, this._endDate);
        }
        if (this._startDate && this._endDate) {
          isInRange =
            currentDate > this._startDate && currentDate < this._endDate;
        }

        weekDays.push({
          date: new Date(currentDate),
          dayNumber: currentDate.getDate(),
          isCurrentMonth,
          isToday,
          isRangeStart,
          isRangeEnd,
          isInRange,
          isDisabled,
          isWeekend
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      this.weeks.push({ days: weekDays });
    }
  }

  /**
   * It is used to verify when the user clicks on the clear button in the input
   * @param value : any
   * @returns void
   */
  onValueChange(value: unknown): void {
    if (value !== undefined) {
      return;
    }

    this._startDate = undefined;
    this._endDate = undefined;
    this.startDateChange.emit(this._startDate);
    this.endDateChange.emit(this._endDate);

    this.emitSelectedRange(true);
    this.onChange({ start: this._startDate, end: this._endDate });
    this.onTouched();
    this.generateWeeks();
  }

  /** Helper methods */
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  private isDateDisabled(date: Date): boolean {
    if (this.disabled) return true;
    if (this.minDate && date < this.minDate) return true;
    if (this.maxDate && date > this.maxDate) return true;
    return false;
  }

  private emitSelectedRange(force = false): void {
    if (force) {
      this.selectedRangeChange.emit({
        start: this._startDate,
        end: this._endDate
      });
      return;
    }
    if (!this._startDate || !this._endDate) return;
    this.selectedRangeChange.emit({
      start: this._startDate,
      end: this._endDate
    });
  }

  /** Event handlers */
  onDateSelect(day: DateRangePickerDay): void {
    if (day.isDisabled || this.disabled) return;

    if (!this._startDate || (this._startDate && this._endDate)) {
      // Start new range or reset if range already full
      this._startDate = day.date;
      this._endDate = undefined;
      this.startDateChange.emit(this._startDate);
      this.endDateChange.emit(this._endDate);
    } else if (day.date < this._startDate) {
      // Clicked before start date -> Reset start date
      this._startDate = day.date;
      this.startDateChange.emit(this._startDate);
    } else if (this.isSameDay(day.date, this._startDate)) {
      // Clicked same day -> toggle off? OR treat as single day range?
      // Let's assume re-clicking start date does nothing or expects end date
      // Usually, clicking same day as start DOES NOTHING until you click another date
      // But some pickers allow start=end. Let's allow start=end if they click again?
      // Actually simplest logic: if we have start but no end, and click >= start, set end.
      this._endDate = day.date;
      this.endDateChange.emit(this._endDate);
      this.isCalendarOpen = false; // Close after selecting end
    } else {
      // Valid end date
      this._endDate = day.date;
      this.endDateChange.emit(this._endDate);
      this.isCalendarOpen = false;
    }
    this.emitSelectedRange();
    this.onChange({ start: this._startDate, end: this._endDate });
    this.onTouched();
    this.generateWeeks();
  }

  onPreviousMonth(): void {
    if (this.disabled) return;
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1
    );
    this.generateWeeks();
    this.monthChange.emit(this.currentMonth);
  }

  onNextMonth(): void {
    if (this.disabled) return;
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1
    );
    this.generateWeeks();
    this.monthChange.emit(this.currentMonth);
  }

  onPreviousYear(): void {
    if (this.disabled) return;
    this.currentMonth = new Date(
      this.currentMonth.getFullYear() - 1,
      this.currentMonth.getMonth(),
      1
    );
    this.generateWeeks();
    this.yearChange.emit(this.currentMonth);
  }

  onNextYear(): void {
    if (this.disabled) return;
    this.currentMonth = new Date(
      this.currentMonth.getFullYear() + 1,
      this.currentMonth.getMonth(),
      1
    );
    this.generateWeeks();
    this.yearChange.emit(this.currentMonth);
  }

  toggleCalendar(): void {
    if (this.disabled) return;

    this.closeOtherCalendars();

    this.isCalendarOpen = !this.isCalendarOpen;
    if (this.isCalendarOpen) {
      this.calculateCalendarPosition();
      this.registerOpenInstance();
      this.onTouched();
      // If we have a start date, make sure we show that month
      if (this._startDate) {
        this.currentMonth = new Date(
          this._startDate.getFullYear(),
          this._startDate.getMonth(),
          1
        );
        this.generateWeeks();
      }
    } else {
      this.unregisterOpenInstance();
    }
  }

  openCalendar(): void {
    if (this.disabled) return;

    this.closeOtherCalendars();

    this.isCalendarOpen = true;
    this.calculateCalendarPosition();
    this.registerOpenInstance();
    this.onTouched();
    if (this._startDate) {
      this.currentMonth = new Date(
        this._startDate.getFullYear(),
        this._startDate.getMonth(),
        1
      );
      this.generateWeeks();
    }
  }

  private closeOtherCalendars(): void {
    UmssDateRangePickerComponent.openInstances.forEach((instance) => {
      if (instance !== this && instance.isCalendarOpen) {
        instance.closeCalendar();
      }
    });
  }

  private registerOpenInstance(): void {
    if (!UmssDateRangePickerComponent.openInstances.includes(this)) {
      UmssDateRangePickerComponent.openInstances.push(this);
    }
  }

  private unregisterOpenInstance(): void {
    const index = UmssDateRangePickerComponent.openInstances.indexOf(this);
    if (index > -1) {
      UmssDateRangePickerComponent.openInstances.splice(index, 1);
    }
  }

  private calculateCalendarPosition(): void {
    requestAnimationFrame(() => {
      const datePickerElement =
        this.elementRef.nativeElement.querySelector('.date-picker');
      if (datePickerElement) {
        const rect = datePickerElement.getBoundingClientRect();
        const calendarHeight = 245; // Height of the calendar
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow >= calendarHeight + 4) {
          this.calendarTop = `${rect.bottom + 4}px`;
        } else if (spaceAbove >= calendarHeight + 4) {
          this.calendarTop = `${rect.top - calendarHeight - 4}px`;
        } else {
          this.calendarTop = `${rect.bottom + 4}px`;
        }

        this.calendarLeft = `${rect.left}px`;
        this.cdr.detectChanges();
      }
    });
  }

  closeCalendar(): void {
    this.isCalendarOpen = false;
    this.unregisterOpenInstance();
    this.cdr.detectChanges();
  }

  private handleFocusIn(event: FocusEvent): void {
    if (!this.isCalendarOpen) {
      return;
    }

    const target = event.target as HTMLElement;
    const focusedDatePicker = target.closest('.date-picker');
    const thisDatePicker =
      this.elementRef.nativeElement.querySelector('.date-picker');
    const focusedCalendar = target.closest('.date-picker-calendar');

    if (focusedDatePicker !== thisDatePicker && !focusedCalendar) {
      this.closeCalendar();
    }
  }

  private handleDocumentClick(event: MouseEvent): void {
    if (!this.isCalendarOpen) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.isCalendarOpen) {
        return;
      }

      const target = event.target as HTMLElement;
      const clickedDatePicker = target.closest('.date-picker');
      const thisDatePicker =
        this.elementRef.nativeElement.querySelector('.date-picker');
      const clickedCalendar = target.closest('.date-picker-calendar');

      if (clickedDatePicker !== thisDatePicker && !clickedCalendar) {
        this.closeCalendar();
      }
    });
  }

  /** ControlValueAccessor implementation */
  writeValue(
    value: { start: Date | undefined; end: Date | undefined } | undefined
  ): void {
    if (value) {
      this._startDate = value.start;
      this._endDate = value.end;
      if (this._startDate) {
        this.currentMonth = new Date(
          this._startDate.getFullYear(),
          this._startDate.getMonth(),
          1
        );
      }
    } else {
      this._startDate = undefined;
      this._endDate = undefined;
    }
    this.generateWeeks();
  }

  registerOnChange(
    fn: (
      value: { start: Date | undefined; end: Date | undefined } | undefined
    ) => void
  ): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** TrackBy functions for performance */
  trackWeek(index: number, week: DateRangePickerWeek): string {
    return week.days[0].date.toISOString();
  }

  trackDay(index: number, day: DateRangePickerDay): string {
    return day.date.toISOString();
  }
}
